import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { NotificationType } from 'common/enums';
import { UserInitials } from 'common/user-initials';
import { CenterModal } from 'pages/album-view-mode/components/center-modal';
import { appActions, AppContext } from 'context/app-context';
import { classnames, getInitials, isEmailValid } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import {
  PRIVATE_SHARE_ERROR,
  ShareEntry,
  ShareLink,
  ShareRole,
  SharesController,
  ShareTarget,
  shareErrorDetails,
} from 'networking/controllers/shares-controller';
import {
  ROLE_OPTIONS,
  roleChoices,
  roleLabel,
  STATUS_LABEL,
  TARGET_LABEL,
} from './roles';
import styles from './share-dialog.module.scss';

interface ShareDialogProps {
  target: ShareTarget;
  /**
   * Other things in the vault to share instead (the Collaborators page offers
   * the whole vault or one of its albums).
   */
  choices?: ShareTarget[];
  onClose: () => void;
  /**
   * Makes the private album or memory visible again so it can be shared.
   * Without it the dialog only explains why sharing is off.
   */
  onMakeVisible?: () => Promise<void>;
  /**
   * The album's sign-up link: anyone with it can add memories without an
   * account (they don't see others'). Albums only.
   */
  contributorLink?: string;
}

// The picker's wording: several vaults (Collaborators page) are named; one
// vault's own entry is "The whole vault".
const choiceLabel = (choice: ShareTarget, all: ShareTarget[]) => {
  const manyVaults = new Set(all.map((c) => c.vaultId)).size > 1;
  if (choice.type === 'vault') {
    return manyVaults ? `Vault: ${choice.name}` : 'The whole vault';
  }
  return `${choice.type === 'album' ? 'Album' : 'Memory'}: ${choice.name}`;
};

// Emails typed or pasted, separated by commas, semicolons or spaces.
const parseEmails = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

interface ShareRowProps {
  entry: ShareEntry;
  targetType: ShareTarget['type'];
  onChangeRole: (entry: ShareEntry, role: ShareRole) => void;
  onRemove: (entry: ShareEntry) => void;
  onResend: (entry: ShareEntry) => void;
}

const ShareRow = ({
  entry,
  targetType,
  onChangeRole,
  onRemove,
  onResend,
}: ShareRowProps) => {
  const { state } = useContext(AppContext);
  const isYou = entry.userId != null && entry.userId === state.data.user.id;
  const display = `${entry.name || entry.email || ''}${isYou ? ' (you)' : ''}`;
  const editable =
    entry.canManage && !entry.inherited && entry.kind !== 'owner';
  // Who the role applies to: on the vault list, someone added to an album or
  // a memory says so.
  const scope =
    entry.inherited || entry.resource.type === targetType
      ? null
      : `${entry.resource.type === 'album' ? 'Album' : 'Memory'}: ${
          entry.resource.name || 'untitled'
        }`;
  const options = roleChoices(entry.role, entry.resource.type);

  return (
    <div className={styles.row}>
      <span className={styles.avatar}>
        <UserInitials name={getInitials(entry.name || entry.email || '')} />
      </span>
      <div className={styles.rowInfo}>
        <span className={styles.rowName}>{display}</span>
        {entry.name && entry.email && (
          <span className={styles.rowEmail}>{entry.email}</span>
        )}
        {(scope || entry.inherited) && (
          <span className={styles.rowScope}>
            {entry.inherited ? 'Through the whole vault' : scope}
          </span>
        )}
      </div>
      {entry.status !== 'active' && (
        <span
          className={classnames(styles.badge, styles[`badge_${entry.status}`])}
        >
          {STATUS_LABEL[entry.status]}
        </span>
      )}
      {editable ? (
        <div className={styles.selectWrap}>
          <select
            className={classnames(styles.select, styles.rowSelect)}
            aria-label={`Role of ${display}`}
            value={entry.role}
            onChange={(e) => onChangeRole(entry, e.target.value as ShareRole)}
          >
            {options.map((option) => (
              <option key={option.role} value={option.role}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <span className={styles.rowRole}>
          {roleLabel(entry.role, entry.resource.type)}
        </span>
      )}
      {editable && (
        <div className={styles.rowActions}>
          {entry.status === 'invited' && (
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => onResend(entry)}
            >
              Resend
            </button>
          )}
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => onRemove(entry)}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
};

export const ShareDialog = ({
  target: initialTarget,
  choices,
  onClose,
  onMakeVisible,
  contributorLink,
}: ShareDialogProps) => {
  const { dispatch } = useContext(AppContext);
  const [target, setTarget] = useState(initialTarget);
  const [isPrivate, setIsPrivate] = useState(Boolean(initialTarget.isPrivate));
  const [shares, setShares] = useState<ShareEntry[]>([]);
  const [link, setLink] = useState<ShareLink>({ enabled: false, url: null });
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [role, setRole] = useState<ShareRole>('viewer');
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [sending, setSending] = useState(false);
  const targetPickerId = useId();
  const linkToggleId = useId();

  const roleOptions = ROLE_OPTIONS[target.type];
  const roleDescription = roleOptions.find((o) => o.role === role)?.description;

  const notify = useCallback(
    (title: string, type: NotificationType, message?: string) =>
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title,
          type,
          message,
        }),
      }),
    [dispatch],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await SharesController.list(target);
      setShares(result.shares);
      setLink(result.link);
    } catch {
      notify('Who has access could not be loaded', NotificationType.Error);
    } finally {
      setLoading(false);
    }
  }, [target, notify]);

  useEffect(() => {
    load();
  }, [load]);

  // A new target can't keep a role it doesn't offer (memories have no editor).
  useEffect(() => {
    if (!ROLE_OPTIONS[target.type].some((o) => o.role === role)) {
      setRole('viewer');
    }
    setIsPrivate(Boolean(target.isPrivate));
  }, [target]);

  const addDraft = () => {
    const parsed = parseEmails(draft);
    if (parsed.length === 0) return true;
    const invalid = parsed.filter((email) => !isEmailValid(email));
    if (invalid.length > 0) {
      notify(`${invalid[0]} isn't an email address`, NotificationType.Error);
      return false;
    }
    setEmails((current) => Array.from(new Set([...current, ...parsed])));
    setDraft('');
    return true;
  };

  const pending = useMemo(() => {
    const parsed = parseEmails(draft).filter((email) => isEmailValid(email));
    return Array.from(new Set([...emails, ...parsed]));
  }, [emails, draft]);

  const send = async () => {
    if (!addDraft()) return;
    if (pending.length === 0) return;
    setSending(true);
    try {
      const updated = await SharesController.invite(target, {
        role,
        invitees: pending.map((email) => ({ email })),
        note: note.trim() || undefined,
      });
      setShares(updated);
      setEmails([]);
      setDraft('');
      setNote('');
      setShowNote(false);
      notify(
        pending.length === 1 ? 'Invitation sent' : 'Invitations sent',
        NotificationType.Success,
      );
    } catch (error) {
      if (shareErrorDetails(error) === PRIVATE_SHARE_ERROR) {
        setIsPrivate(true);
        return;
      }
      notify(
        'The invitation could not be sent',
        NotificationType.Error,
        shareErrorDetails(error) || undefined,
      );
    } finally {
      setSending(false);
    }
  };

  const changeRole = async (entry: ShareEntry, newRole: ShareRole) => {
    try {
      const updated = await SharesController.changeRole(entry.id, newRole);
      setShares((list) =>
        list.map((item) =>
          item.id === entry.id ? { ...item, ...updated } : item,
        ),
      );
      // Takes effect right away; nothing is emailed.
      notify(
        `${entry.name || entry.email} is now ${roleLabel(
          newRole,
          entry.resource.type,
        ).toLowerCase()}`,
        NotificationType.Success,
        'The change applies right away.',
      );
    } catch (error) {
      notify(
        'The role could not be changed',
        NotificationType.Error,
        shareErrorDetails(error) || undefined,
      );
    }
  };

  const remove = async (entry: ShareEntry) => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(`Remove ${entry.name || entry.email}? They lose access.`)
    ) {
      return;
    }
    try {
      await SharesController.remove(entry.id);
      setShares((list) => list.filter((item) => item.id !== entry.id));
    } catch {
      notify('They could not be removed', NotificationType.Error);
    }
  };

  const resend = async (entry: ShareEntry) => {
    try {
      await SharesController.resend(entry.id);
      notify('Invitation sent again', NotificationType.Success);
    } catch {
      notify('The invitation could not be sent', NotificationType.Error);
    }
  };

  const updateLink = async (change: {
    enabled?: boolean;
    regenerate?: boolean;
  }) => {
    try {
      setLink(await SharesController.updateLink(target, change));
      if (change.regenerate) {
        notify(
          'New link created',
          NotificationType.Success,
          'The old link no longer works.',
        );
      }
    } catch (error) {
      if (shareErrorDetails(error) === PRIVATE_SHARE_ERROR) setIsPrivate(true);
      else notify('The link could not be changed', NotificationType.Error);
    }
  };

  const copyLink = async (url = link.url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      notify('Link copied', NotificationType.Success);
    } catch {
      notify('The link could not be copied', NotificationType.Error);
    }
  };

  const makeVisible = async () => {
    if (!onMakeVisible) return;
    try {
      await onMakeVisible();
      setIsPrivate(false);
      setTarget((current) => ({ ...current, isPrivate: false }));
    } catch {
      notify('It could not be made visible', NotificationType.Error);
    }
  };

  // Active and invited people first; removed and declined at the end.
  const visibleShares = useMemo(() => {
    const rank = (entry: ShareEntry) =>
      ['active', 'invited'].includes(entry.status) ? 0 : 1;
    return [...shares]
      .filter((entry) => entry.status !== 'removed')
      .sort((a, b) => rank(a) - rank(b));
  }, [shares]);

  const what = target.type === 'memory' ? 'memory' : target.type;

  return (
    <CenterModal onClose={onClose} className={styles.modal}>
      <div className={styles.body}>
        <div className={styles.header}>
          <span className={styles.eyebrow}>{TARGET_LABEL[target.type]}</span>
          <span className={styles.title}>Share “{target.name}”</span>
        </div>

        {choices && choices.length > 1 && (
          <label className={styles.field} htmlFor={targetPickerId}>
            <span className={styles.fieldLabel}>Share</span>
            <div className={styles.selectWrap}>
              <select
                id={targetPickerId}
                className={styles.select}
                value={`${choices.findIndex(
                  (c) =>
                    c.type === target.type &&
                    c.id === target.id &&
                    c.vaultId === target.vaultId,
                )}`}
                onChange={(e) => setTarget(choices[Number(e.target.value)])}
              >
                {choices.map((choice, index) => (
                  <option key={`${choice.type}-${choice.id}`} value={index}>
                    {choiceLabel(choice, choices)}
                  </option>
                ))}
              </select>
            </div>
          </label>
        )}

        {isPrivate ? (
          <div className={styles.privateNotice}>
            <span className={styles.privateTitle}>This {what} is private</span>
            <span className={styles.privateText}>
              Private {what === 'album' ? 'albums' : 'memories'} can&apos;t be
              shared. Make it not private first: everyone who can see the vault
              will then see it too.
            </span>
            {onMakeVisible && (
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={makeVisible}
              >
                Make it not private
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.invite}>
              <div className={styles.chipsInput}>
                {emails.map((email) => (
                  <span key={email} className={styles.chip}>
                    {email}
                    <button
                      type="button"
                      aria-label={`Remove ${email}`}
                      className={styles.chipRemove}
                      onClick={() =>
                        setEmails((list) => list.filter((e) => e !== email))
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="email"
                  className={styles.chipsField}
                  value={draft}
                  placeholder={emails.length ? '' : 'Add people by email'}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={addDraft}
                  onKeyDown={(e) => {
                    if (['Enter', ',', ' ', 'Tab'].includes(e.key) && draft) {
                      e.preventDefault();
                      addDraft();
                    } else if (
                      e.key === 'Backspace' &&
                      !draft &&
                      emails.length
                    ) {
                      setEmails((list) => list.slice(0, -1));
                    }
                  }}
                />
              </div>
              <div className={styles.inviteControls}>
                <div className={styles.selectWrap}>
                  <select
                    className={styles.select}
                    aria-label="Role"
                    value={role}
                    onChange={(e) => setRole(e.target.value as ShareRole)}
                  >
                    {roleOptions.map((option) => (
                      <option key={option.role} value={option.role}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={send}
                  disabled={sending || pending.length === 0}
                >
                  {sending ? 'Sending…' : 'Send invite'}
                </button>
              </div>
              {roleDescription && (
                <span className={styles.hint}>{roleDescription}</span>
              )}
              {showNote ? (
                <textarea
                  className={styles.note}
                  value={note}
                  maxLength={500}
                  placeholder="Add a message (optional)"
                  onChange={(e) => setNote(e.target.value)}
                />
              ) : (
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setShowNote(true)}
                >
                  Add a message
                </button>
              )}
            </div>

            <div className={styles.section}>
              <span className={styles.sectionTitle}>People with access</span>
              {loading && <span className={styles.muted}>Loading…</span>}
              {!loading && visibleShares.length === 0 && (
                <span className={styles.muted}>
                  Only you have access so far.
                </span>
              )}
              {visibleShares.map((entry) => (
                <ShareRow
                  key={entry.id}
                  entry={entry}
                  targetType={target.type}
                  onChangeRole={changeRole}
                  onRemove={remove}
                  onResend={resend}
                />
              ))}
            </div>

            <div className={styles.section}>
              <span className={styles.sectionTitle}>Link</span>
              <label className={styles.linkRow} htmlFor={linkToggleId}>
                <input
                  id={linkToggleId}
                  type="checkbox"
                  className={styles.toggle}
                  checked={link.enabled}
                  onChange={(e) => updateLink({ enabled: e.target.checked })}
                />
                <span className={styles.linkText}>
                  Anyone with the link can view this {what} (not its private
                  memories).
                  {!link.enabled && (
                    <span className={styles.linkOff}>
                      {' '}
                      Off: only the people above can open it.
                    </span>
                  )}
                </span>
              </label>
              {link.enabled && link.url && (
                <div className={styles.linkActions}>
                  <input
                    className={styles.linkField}
                    readOnly
                    value={link.url}
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => copyLink()}
                  >
                    Copy link
                  </button>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => updateLink({ regenerate: true })}
                  >
                    New link
                  </button>
                </div>
              )}
              {contributorLink && target.type === 'album' && (
                <div className={styles.contributorLink}>
                  <span className={styles.linkText}>
                    Contributor link: anyone with it can add memories to this
                    album without an account, and doesn&apos;t see the others.
                  </span>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => copyLink(contributorLink)}
                  >
                    Copy contributor link
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </CenterModal>
  );
};
